import { useInstall } from '../lib/install.tsx';
import { LEAGUE } from '@ff/core';

/**
 * "Put the app on your phone."
 *
 * Android and desktop hand us a real install prompt. iOS never will — there is
 * no API for it, only the Share sheet — so on an iPhone this is a set of
 * instructions, and they have to be right, because on iOS installing is also the
 * only way notifications work at all.
 */
export function InstallCoach({ reason }: { reason?: string }) {
  const { platform, promptInstall } = useInstall();

  if (platform === 'installed' || platform === 'unavailable') return null;

  if (platform === 'prompt') {
    return (
      <div className="card space-y-3 p-6">
        <div>
          <p className="font-display text-lg font-bold">Install {LEAGUE.bookName}</p>
          <p className="mt-1 text-sm text-chalk-dim">
            {reason ?? 'Keep it a tap away, in its own window, with notifications.'}
          </p>
        </div>
        <button
          onClick={() => void promptInstall()}
          className="rounded-md bg-brass px-3 py-1.5 font-display text-sm font-bold text-chalk hover:opacity-90"
        >
          Install
        </button>
      </div>
    );
  }

  if (platform === 'ios-other') {
    return (
      <div className="card space-y-2 p-6">
        <p className="font-display text-lg font-bold">Open this in Safari</p>
        <p className="text-sm text-chalk-dim">
          {reason ? `${reason} ` : ''}
          On an iPhone, only Safari can add {LEAGUE.bookName} to your home screen. Open
          this same page in Safari and the steps will be here.
        </p>
      </div>
    );
  }

  // iOS Safari: the Share sheet, spelled out.
  return (
    <div className="card space-y-3 p-6">
      <div>
        <p className="font-display text-lg font-bold">
          Add {LEAGUE.bookName} to your home screen
        </p>
        <p className="mt-1 text-sm text-chalk-dim">
          {reason ?? 'It opens like an app, without the browser bars.'}
        </p>
      </div>
      <ol className="space-y-2 text-sm text-chalk-dim">
        <li>
          <span className="font-mono text-xs text-brass">1.</span> Tap the Share
          button at the bottom of Safari — the square with the arrow coming out.
        </li>
        <li>
          <span className="font-mono text-xs text-brass">2.</span> Scroll down and
          tap <span className="text-chalk">Add to Home Screen</span>.
        </li>
        <li>
          <span className="font-mono text-xs text-brass">3.</span> Tap{' '}
          <span className="text-chalk">Add</span>, then open {LEAGUE.bookName} from your
          home screen.
        </li>
      </ol>
      <p className="font-mono text-xs text-chalk-dim">
        You'll sign in once more inside the app — iOS keeps it separate from
        Safari.
      </p>
    </div>
  );
}
